import React, { useEffect, useState } from 'react';
import { healthsherpa } from './api';
import { targetLabel } from './hsCsv';
import { fs } from './hsScale';

// 5.46.0 admin cards for HealthSherpa → Settings: CMS plan data + SBC library status, the confirmed personal-info
// columns, the personal-info Show log, and claimed clients with missing / stale export data or missing dates of birth.

const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
const PUF_LABEL = { 'plan-attributes-puf': 'Plan attributes', 'benefits-and-cost-sharing-puf': 'Copays', 'network-puf': 'Networks',
  'plan-id-crosswalk-puf': 'Crosswalk' };
const PURPOSE = { show: 'Show', quote_prefill: 'Quote prefill', enroll_prefill: 'Enrollment prefill', staff_entry: 'Staff entry' };
const th = { textAlign: 'left', padding: '3px 10px 3px 0', fontWeight: 600 };
const td = { padding: '3px 10px 3px 0', verticalAlign: 'top' };

export function CmsStatusCard({ c, Card }) {
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { healthsherpa.coverage.cmsStatus().then((r) => setS(r.data)).catch((e) => setError(detailOf(e))); }, []);
  const job = s && s.sbc_job;
  return (
    <Card c={c} title="Plan data (CMS, Texas) and SBC library">
      {error && <div role="alert" style={{ color: '#c0392b', fontSize: fs(13) }}>{error}</div>}
      {!s && !error && <div style={{ fontSize: fs(13) }}>Loading…</div>}
      {s && (
        <div style={{ fontSize: fs(13) }} data-testid="hs-cms-status">
          <table style={{ borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Plan year</th><th style={th}>Plans</th><th style={th}>Crosswalk</th><th style={th}>Files</th><th style={th}>SBCs saved / failed / links</th></tr></thead>
            <tbody>
              {s.years.map((y) => (
                <tr key={y.plan_year} data-testid="hs-cms-year" data-year={y.plan_year}>
                  <td style={td}><strong>{y.plan_year}</strong></td>
                  <td style={td}>{y.published === false ? <span style={{ color: '#b9770e' }}>not yet published</span> : y.plans.toLocaleString()}</td>
                  <td style={td}>{y.crosswalk.toLocaleString()}</td>
                  <td style={td}>{Object.entries(y.pufs).map(([k, v]) => `${PUF_LABEL[k]}: ${v ? v.status : '—'}`).join(' · ')}</td>
                  <td style={td}>{y.sbc_ok.toLocaleString()} / {y.sbc_failed.toLocaleString()} / {y.sbc_urls.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 6, color: c.textSecondary }}>Last load: {s.last_load_label || 'never'}</div>
          <div style={{ marginTop: 4, color: c.textSecondary }} data-testid="hs-cms-job">
            SBC download job: {job ? `${job.state} — ${job.done} of ${job.total} links, ${job.ok} saved, ${job.failed} failed, ${(job.bytes / 1e6).toFixed(1)} MB` : 'never run'}
          </div>
          <div style={{ marginTop: 6, fontSize: fs(11), color: c.textSecondary }}>Yearly reload (admin, on the server): <code>{s.reload_command}</code></div>
        </div>
      )}
    </Card>
  );
}

export function PiColumnsCard({ c, Card }) {
  const [m, setM] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { healthsherpa.coverage.piColumns().then((r) => setM(r.data)).catch((e) => setError(detailOf(e))); }, []);
  return (
    <Card c={c} title="Personal info — confirmed export columns">
      <div style={{ fontSize: fs(12), color: c.textSecondary, marginBottom: 6 }}>
        Henry 2026-11-02: dates of birth, tobacco, gender and household income / size are kept from the export for quoting
        (masked until Show). SSNs are never kept. New columns are confirmed on the upload card.
      </div>
      {error && <div role="alert" style={{ color: '#c0392b', fontSize: fs(13) }}>{error}</div>}
      {m && m.columns.length === 0 && <div style={{ fontSize: fs(13) }}>No columns confirmed yet.</div>}
      {m && m.columns.length > 0 && (
        <table style={{ borderCollapse: 'collapse', fontSize: fs(13) }} data-testid="hs-pi-columns">
          <thead><tr><th style={th}>Column</th><th style={th}>Kept as</th><th style={th}>Confirmed</th></tr></thead>
          <tbody>{m.columns.map((x) => (
            <tr key={x.source_column}><td style={{ ...td, fontFamily: 'monospace' }}>{x.source_column}</td><td style={td}>{targetLabel(x.target)}</td>
              <td style={td}>{x.confirmed_label} · {x.confirmed_by || '—'}</td></tr>))}</tbody>
        </table>
      )}
    </Card>
  );
}

export function PiViewLogCard({ c, Card }) {
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { healthsherpa.coverage.piViews(page).then((r) => setD(r.data)).catch((e) => setError(detailOf(e))); }, [page]);
  const pages = d ? Math.max(1, Math.ceil(d.total / d.per_page)) : 1;
  return (
    <Card c={c} title="Personal info — who looked (Show log)">
      {error && <div role="alert" style={{ color: '#c0392b', fontSize: fs(13) }}>{error}</div>}
      {d && d.rows.length === 0 && <div style={{ fontSize: fs(13) }}>Nobody has shown personal info yet.</div>}
      {d && d.rows.length > 0 && (
        <>
          <table style={{ borderCollapse: 'collapse', fontSize: fs(13) }} data-testid="hs-pi-log">
            <thead><tr><th style={th}>When (CT)</th><th style={th}>Who</th><th style={th}>Client</th><th style={th}>What</th></tr></thead>
            <tbody>{d.rows.map((r) => (
              <tr key={r.id} data-testid="hs-pi-log-row" data-purpose={r.purpose}><td style={td}>{r.at_label}</td><td style={td}>{r.user || '—'}</td>
                <td style={td}>{r.person || `#${r.person_id}`}</td><td style={td}>{PURPOSE[r.purpose] || r.purpose}</td></tr>))}</tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: fs(12) }}>
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>◀</button>
            <span>Page {page} of {pages} · {d.total.toLocaleString()} entries</span>
            <button disabled={page >= pages} onClick={() => setPage(page + 1)}>▶</button>
          </div>
        </>
      )}
    </Card>
  );
}

export function MissingDataCard({ c, Card }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { healthsherpa.coverage.missing().then((r) => setD(r.data)).catch((e) => setError(detailOf(e))); }, []);
  return (
    <Card c={c} title="Claimed clients with missing or stale data">
      {error && <div role="alert" style={{ color: '#c0392b', fontSize: fs(13) }}>{error}</div>}
      {d && (
        <div style={{ fontSize: fs(13) }} data-testid="hs-missing-list">
          <div style={{ color: c.textSecondary, marginBottom: 6 }}>
            {d.plan_year} coverage: no export row, an export older than {d.stale_days} days, or a missing date of birth. {d.total} client{d.total === 1 ? '' : 's'}.
          </div>
          {d.rows.length > 0 && (
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Client</th><th style={th}>Owner</th><th style={th}>Export</th><th style={th}>Dates of birth missing</th></tr></thead>
              <tbody>{d.rows.map((r) => (
                <tr key={r.person_id} data-testid="hs-missing-row" data-pid={r.person_id}>
                  <td style={td}>{r.display_name}</td><td style={td}>{r.owner || '—'}</td>
                  <td style={td}>{r.flags.map((f) => f.label).join(' · ') || 'OK'}</td><td style={td}>{r.missing_dob || '—'}</td>
                </tr>))}</tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}
